import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { XMarkIcon, ShieldCheckIcon, ChartBarIcon, CogIcon, MegaphoneIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import {
  COOKIE_CATEGORIES,
  getConsentPreferences,
  saveConsentPreferences,
  acceptAllCookies,
  rejectAllCookies,
  cleanupCookiesBasedOnConsent,
  hasUserConsented
} from '../../utils/cookieConsent';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const categoryIcons = {
  necessary: ShieldCheckIcon,
  analytics: ChartBarIcon,
  functional: CogIcon,
  marketing: MegaphoneIcon
};

export default function GranularCookieConsent({ onConsentUpdate }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(getConsentPreferences());

  useEffect(() => {
    // Check if user has already consented
    if (!hasUserConsented()) {
      setOpen(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const newPreferences = acceptAllCookies();
    setPreferences(newPreferences);
    cleanupCookiesBasedOnConsent();
    setOpen(false);
    if (onConsentUpdate) onConsentUpdate(newPreferences);
  };

  const handleRejectAll = () => {
    const newPreferences = rejectAllCookies();
    setPreferences(newPreferences);
    cleanupCookiesBasedOnConsent();
    setOpen(false);
    if (onConsentUpdate) onConsentUpdate(newPreferences);
  };

  const handleSavePreferences = () => {
    const newPreferences = saveConsentPreferences(preferences);
    cleanupCookiesBasedOnConsent();
    setOpen(false);
    if (onConsentUpdate) onConsentUpdate(newPreferences);
  };

  const toggleCategory = (category) => {
    if (COOKIE_CATEGORIES[category].required) return;

    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => { }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white border-2 border-black px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-2xl font-bold leading-6 text-black">
                      {t('CookieConsent.Title', 'Your Privacy Matters')}
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="text-sm text-gray-800">
                        {t('CookieConsent.Description', 'We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts. You can customize your preferences below.')}
                      </p>
                    </div>
                  </div>
                </div>

                {!showDetails ? (
                  <div className="mt-8 space-y-4">
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                      <button
                        type="button"
                        className="border-2 border-black bg-white text-black rounded-md px-6 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors duration-200"
                        onClick={handleRejectAll}
                      >
                        {t('CookieConsent.RejectAll', 'Reject All')}
                      </button>
                      <button
                        type="button"
                        className="border-2 border-black bg-white text-black rounded-md px-6 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors duration-200"
                        onClick={() => setShowDetails(true)}
                      >
                        {t('CookieConsent.Customize', 'Customize')}
                      </button>
                      <button
                        type="button"
                        data-testid="cookie-accept-all"
                        className="bg-black text-white rounded-md px-6 py-3 text-sm font-medium border-2 border-black hover:bg-gray-800 transition-colors duration-200"
                        onClick={handleAcceptAll}
                      >
                        {t('CookieConsent.AcceptAll', 'Accept All')}
                      </button>
                    </div>
                    <div className="text-center mt-4">
                      <a href="/privacy-policy" className="text-xs text-black hover:underline font-medium">
                        {t('CookieConsent.PrivacyPolicy', 'Privacy Policy')}
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6">
                    <div className="space-y-4">
                      {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => {
                        const Icon = categoryIcons[key];
                        return (
                          <div
                            key={key}
                            className={classNames(
                              'rounded-lg border-2 p-4',
                              category.required ? 'border-gray-300 bg-gray-50' : 'border-black bg-white'
                            )}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <Icon className="h-6 w-6 text-black" aria-hidden="true" />
                                <div>
                                  <h4 className="text-sm font-bold text-black">{t(category.nameKey)}</h4>
                                  <p className="mt-1 text-sm text-gray-700">{t(category.descriptionKey)}</p>
                                  {category.required && (
                                    <p className="mt-1 text-xs text-gray-500 font-medium">
                                      {t('CookieConsent.AlwaysActive', 'Always active')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Switch
                                checked={preferences[key]}
                                onChange={() => toggleCategory(key)}
                                disabled={category.required}
                                className={classNames(
                                  preferences[key] ? 'bg-black' : 'bg-gray-200',
                                  category.required ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                                  'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2'
                                )}
                              >
                                <span
                                  aria-hidden="true"
                                  className={classNames(
                                    preferences[key] ? 'translate-x-5' : 'translate-x-0',
                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                  )}
                                />
                              </Switch>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3">
                      <button
                        type="button"
                        className="border-2 border-black bg-white text-black rounded-md px-6 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors duration-200"
                        onClick={() => setShowDetails(false)}
                      >
                        {t('CookieConsent.Back', 'Back')}
                      </button>
                      <button
                        type="button"
                        className="bg-black text-white rounded-md px-6 py-3 text-sm font-medium border-2 border-black hover:bg-gray-800 transition-colors duration-200"
                        onClick={handleSavePreferences}
                      >
                        {t('CookieConsent.SavePreferences', 'Save Preferences')}
                      </button>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
